import {
  type ChangeEventHandler,
  type ComponentProps,
  type FocusEventHandler,
  type KeyboardEventHandler,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { cn } from '@air/utils-tailwind';

import { InlineError } from './InlineError';
import { removeNewlines } from './utils';

export type UnstyledInlineInputProps = {
  error?: string;
  onCancel?: () => void;
  onSubmit?: (value: string) => void;
  onChange?: (value: string) => void;
  defaultValue?: string;
  /** This method should return error string or undefined if value is valid */
  validate?: (value: string) => string | undefined;
  inputClassName?: string;
  label: string;
  allowNewLines?: boolean;
  'data-testid'?: string;
} & Omit<ComponentProps<'textarea'>, 'onSubmit' | 'value' | 'onKeyDown' | 'onKeyUp' | 'onChange' | 'hasError'>;

export const UnstyledInlineInput = memo(
  ({
    error,
    onCancel,
    onSubmit,
    onChange,
    defaultValue = '',
    validate,
    className,
    inputClassName,
    label,
    allowNewLines = true,
    style,
    ...restProps
  }: UnstyledInlineInputProps) => {
    const [value, setValue] = useState(defaultValue);
    const [internalError, setInternalError] = useState<string | undefined>();
    const isEscPressedRef = useRef(false);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const errorToShow = error || internalError;
    const hasError = !!errorToShow;

    useEffect(() => {
      setValue(defaultValue);
    }, [defaultValue]);

    const submitValue = useCallback(
      (value: string) => {
        if (!hasError) {
          onSubmit?.(value?.trim());
        }
      },
      [hasError, onSubmit],
    );

    const handleEscOnKeyUp = useCallback<KeyboardEventHandler<HTMLTextAreaElement>>(
      (event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          isEscPressedRef.current = true;
          onCancel?.();
        }
      },
      [onCancel],
    );

    const handleBlur = useCallback<FocusEventHandler<HTMLTextAreaElement>>(() => {
      if (!isEscPressedRef.current) {
        submitValue(value);
      }
      isEscPressedRef.current = false;
    }, [submitValue, value]);

    const onValueChange = useCallback(
      (fieldValue: string) => {
        setValue(fieldValue);
        onChange?.(fieldValue);

        if (validate) {
          setInternalError(validate(fieldValue));
        } else {
          setInternalError(undefined);
        }
      },
      [onChange, validate],
    );

    const onInputChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>(
      (event) => {
        event.target.value = allowNewLines ? event.target.value : removeNewlines(event.target.value);

        onValueChange(event.target.value);
      },
      [onValueChange, allowNewLines],
    );

    const handleKeyDown = useCallback<KeyboardEventHandler<HTMLTextAreaElement>>(
      (event) => {
        if (event.key === 'Enter') {
          event.stopPropagation();
          if (event.shiftKey) {
            if (!allowNewLines) {
              event.preventDefault();
            }
          } else {
            event.preventDefault();
            textAreaRef.current?.blur();
          }
        }

        if (event.key === 'Enter' && !event.shiftKey) {
          event.stopPropagation();
          event.preventDefault();
          textAreaRef.current?.blur();
        }
      },
      [allowNewLines],
    );

    const handleFocus = useCallback<FocusEventHandler<HTMLTextAreaElement>>((event) => {
      const textareaValue = event.target.value;
      event.target.setSelectionRange(textareaValue.length, textareaValue.length);
    }, []);

    return (
      <div className={cn('relative flex flex-1 flex-col', className)}>
        <textarea
          aria-label={label}
          className={cn(
            'resize-none bg-gray-1 leading-[inherit] tracking-[inherit] outline-none',
            allowNewLines ? 'whitespace-pre-wrap' : 'whitespace-nowrap',
            inputClassName,
          )}
          data-error={!!errorToShow}
          onBlur={handleBlur}
          onChange={onInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          onKeyUp={handleEscOnKeyUp}
          ref={textAreaRef}
          style={{
            fontSize: 'inherit',
            ...style,
          }}
          value={value}
          {...restProps}
        />
        <InlineError
          data-testid={restProps['data-testid'] ? `${restProps['data-testid']}-error` : 'INLINE_INPUT_ERROR'}
          error={errorToShow}
        />
      </div>
    );
  },
);

UnstyledInlineInput.displayName = 'UnstyledInlineInput';
